import React, { forwardRef, useImperativeHandle, useState, useEffect } from 'react'
import Dialog from '@material-ui/core/Dialog'
import CloseIcon from '@material-ui/icons/Close'
import IconButton from '@material-ui/core/IconButton'
import { useTranslation } from 'react-i18next'
import Moment from 'moment'
import { extendMoment } from 'moment-range'
import { useDispatch } from 'react-redux'

import styles from './styles.module.scss'

import { getShifts, addShiftTimeline } from '../../api'
import { addSnackbar, dismissSnackbar } from '../../store/organizationList/actions'

import CopyIcon from '../Icons/CopyIcon'
import InputSelect from '../Core/InputSelect'
import Label from '../Core/InputLabel'
import Input from '../Core/Input/Input'
import Button from '../Core/Button/Button'
import RadioButton from '../Core/Radio'

const moment = extendMoment(Moment)

const ArrowIcon = props => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={16}
      height={16}
      viewBox="0 0 24 24"
      fill="none"
      stroke="#0085ff"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      aria-hidden="true"
      data-tsd-source="/src/components/CopyScheduleModal.tsx:125:13"
      {...props}
    >
      <path d="M5 12h14M12 5l7 7-7 7" />
    </svg>
  )
}

const AttentionIcon = props => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={16}
      height={16}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      aria-hidden="true"
      data-tsd-source="/src/components/CopyScheduleModal.tsx:208:15"
      {...props}
    >
      <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3M12 9v4M12 17h.01" />
    </svg>
  )
}

const CopySchedule = forwardRef(({companyId, currentDate, targetEvents, onCopy}, ref) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  moment.locale(localStorage.getItem('i18nextLng') || 'en')

  const [open, setOpen] = useState(false)
  const [form, setForm] = useState({
    sourcePeriod: moment(currentDate, 'YYYY-MM-DD').subtract(1, 'month').startOf('month').format('YYYY-MM'),
    sourceStartDate: 1,
    days: Math.min(moment(currentDate, 'YYYY-MM-DD').subtract(1, 'month').daysInMonth() - 1, moment(currentDate, 'YYYY-MM').daysInMonth() - 1) + 1,
    targetDateStart: 1,
    overwriteExisting: 'overwrite',
  })
  const [loading, setLoading] = useState(false)
  const [sourceEvents, setSourceEvents] = useState([])
  const [shift, setShift] = useState(null)

  const sourceMonthDays = moment(form.sourcePeriod, 'YYYY-MM').daysInMonth()
  const targetMonthDays = moment(currentDate, 'YYYY-MM').daysInMonth()
  const maxDays = Math.min(sourceMonthDays - form.sourceStartDate, targetMonthDays - form.targetDateStart) + 1

  const monthOptions = Array.from(moment.rangeFromInterval('month', 5, moment(currentDate, 'YYYY-MM-DD').subtract(6, 'month')).by('month')).reverse().map(date => {
    return {
      id: date.format('YYYY-MM'),
      name: date.format('MMMM YYYY'),
    }
  })

  useImperativeHandle(ref, () => ({
    open: (shift) => {
      setOpen(true)
      setShift(shift || null)
    }
  }))

  // useEffect(() => {
  //   if (form.days > maxDays) {
  //     setForm(prev => ({
  //       ...prev,
  //       days: maxDays,
  //     }))
  //   }
  // }, [maxDays, form.days])

  useEffect(() => {
    if (open) {
      getShiftsEvents()
    }
  }, [form.sourcePeriod, open])

  const getShiftsEvents = async () => {
    const params = {
      type: 'period',
      from_date: moment(form.sourcePeriod, 'YYYY-MM').format('YYYY-MM-DD'),
      to_date: moment(form.sourcePeriod, 'YYYY-MM').endOf('month').format('YYYY-MM-DD'),
      shiftTypeArr: [],
      employeesArr: [],
      placesArr: [],
    }
    const res = await getShifts(companyId, params)
    if (Array.isArray(res?.events)) {
      setSourceEvents(res.events.filter(event => !event.empty_manual && !event.empty_employee))
    }
  }

  const handleClose = () => {
    setOpen(false)
  }

  const handleChangeForm = ({target}) => {
    const { name, value } = target
    let days = form.days
    let currentMaxDays = maxDays
    if (name === 'targetDateStart') {
      currentMaxDays = Math.min(sourceMonthDays - form.sourceStartDate, targetMonthDays - value) + 1
    }
    if (name === 'sourceStartDate') {
      currentMaxDays = Math.min(sourceMonthDays - value, targetMonthDays - form.targetDateStart) + 1
    }
    if (days > currentMaxDays) {
      days = currentMaxDays
    }
    setForm(prev => ({
      ...prev,
      days: days,
      [name]: value,
    }))
  }

  const handleChangeOverwrite = ({target}) => {
    setForm(prev => ({
      ...prev,
      overwriteExisting: target.value,
    }))
  }

  const handleCopySchedule = async () => {
    const sourceStartDate = moment(`${form.sourcePeriod}-${form.sourceStartDate}`, 'YYYY-MM-DD')
    const sourceEndDate = moment(`${form.sourcePeriod}-${form.sourceStartDate}`, 'YYYY-MM-DD').add(form.days, 'days')
    const targetStartDate = moment(`${moment(currentDate, 'YYYY-MM').format('YYYY-MM')}-${form.targetDateStart}`, 'YYYY-MM-DD')
    const events = sourceEvents.filter(event => {
      const eventStart = moment(event.start, 'YYYY-MM-DD HH:mm:ss')
      return eventStart.isBetween(sourceStartDate, sourceEndDate, null, '[]')
    })
    const eventsToCreate = events.map(event => {
      const eventStart = moment(event.start, 'YYYY-MM-DD HH:mm:ss')
      const eventEnd = moment(event.end, 'YYYY-MM-DD HH:mm:ss')
      const diff = eventStart.diff(sourceStartDate, 'days')
      const newStart = targetStartDate.clone().add(diff, 'days').format('YYYY-MM-DD HH:mm')
      const newEnd = targetStartDate.clone().add(diff, 'days').add(eventEnd.diff(eventStart)).format('YYYY-MM-DD HH:mm')
      const [shiftId] = event.resourceId.split('-')
      if (shift && shift.id !== shiftId*1) {
        return {}
      }
      const targetEvent = targetEvents.find(targetEvent => targetEvent.resourceId === event.resourceId && moment(targetEvent.start).isSame(moment(newStart, 'YYYY-MM-DD HH:mm'), 'day') && (targetEvent.empty_manual || form.overwriteExisting === 'overwrite'))
      return {
        shiftId: shiftId,
        data: targetEvent?.id,
        dateTime_start: newStart,
        dateTime_end: newEnd,
      }
    }).filter(event => Boolean(event.data))

    setLoading(true)
    const res = await Promise.all(
      eventsToCreate.map(event => addShiftTimeline(companyId, event.shiftId, {data: event.data, dateTime_start: event.dateTime_start, dateTime_end: event.dateTime_end}))
    )
    setLoading(false)
    if (res.every(res => res.status)) {
      dispatch(addSnackbar(t('Schedule copied successful'), 'success'))
      setTimeout(() => {
        dispatch(dismissSnackbar())
      }, 2000)
      onCopy()
    }
  }

  const sourceStartDate = moment(`${form.sourcePeriod}-${form.sourceStartDate}`, 'YYYY-MM-DD')
  const sourceEndDate = moment(`${form.sourcePeriod}-${form.sourceStartDate}`, 'YYYY-MM-DD').add(form.days - 1, 'days')
  const targetStartDate = moment(`${moment(currentDate, 'YYYY-MM').format('YYYY-MM')}-${form.targetDateStart}`, 'YYYY-MM-DD')
  const targetEndDate = moment(`${moment(currentDate, 'YYYY-MM').format('YYYY-MM')}-${form.targetDateStart}`, 'YYYY-MM-DD').add(form.days - 1, 'days')
  const sourceStartWeekDay = sourceStartDate.format('ddd')
  const targetStartWeekDay = targetStartDate.format('ddd')

  return (
    <Dialog  onClose={handleClose} open={open} maxWidth='lg'>
      <div className={styles.container}>
        <div className={styles.header}>
          <CopyIcon width={24} height={24} color="#0085ff" />
          <div className={styles.label}>{t(`Copy {{shiftName}} schedule from previous period`, {shiftName: shift ? shift.name : ''})}</div>
          <IconButton aria-label='close' onClick={handleClose} className={styles.closeButton}>
            <CloseIcon />
          </IconButton>
        </div>
        <div className={styles.infoContainer}>
          <div className={styles.infoItem}>
            <div className={styles.infoTitle}>{t('Copy from')}</div>
            <div className={styles.infoStartTime}>{sourceStartDate.format('DD MMM YYYY')}<span>{` (${sourceStartDate.format('ddd')})`}</span></div>
            <div className={styles.infoEndTime}>{t('to')} {sourceEndDate.format('DD MMM YYYY (ddd)')} · {form.days} {t('days')}</div>
          </div>
          <ArrowIcon />
          <div className={styles.infoItem}>
            <div className={styles.infoTitle}>{t('Paste into')}</div>
            <div className={styles.infoStartTime}>{targetStartDate.format('DD MMM YYYY')}<span>{` (${targetStartDate.format('ddd')})`}</span></div>
            <div className={styles.infoEndTime}>{t('to')} {targetEndDate.format('DD MMM YYYY (ddd)')}</div>
          </div>
        </div>
        <div className={styles.formContainer}>
          <div className={styles.formItem}>
            <Label text={t('Source period')} htmlFor='sourcePeriod' />
            <InputSelect
              id='sourcePeriod'
              placeholder={t('Select month')}
              options={monthOptions}
              value={form.sourcePeriod}
              name='sourcePeriod'
              onChange={handleChangeForm}
              valueKey='id'
              labelKey='name' />
          </div>
          <div className={styles.formItem}>
            <Label text={t('Source start day')} htmlFor='sourceStartDate' />
            <Input
              id='sourceStartDate'
              name='sourceStartDate'
              type='number'
              placeholder={t('Source start day')}
              value={form.sourceStartDate}
              onChange={handleChangeForm}
              max={sourceMonthDays}
              fullWidth />
            <div className={styles.description}>
              {t('{{month}} has {{days}} days', {month: moment(form.sourcePeriod, 'YYYY-MM').format('MMMM'), days: sourceMonthDays})}
            </div>
          </div>
          <div className={styles.formItem}>
            <Label text={t('Length (days)')} htmlFor='days' />
            <Input
              id='days'
              name='days'
              type='number'
              placeholder={t('Length (days)')}
              value={form.days}
              onChange={handleChangeForm}
              max={maxDays}
              fullWidth />
          </div>
          <div className={styles.formItem}>
            <Label text={t(`Target start day ({{date}})`, {date: moment(currentDate).format('MMMM YYYY')})} htmlFor='targetDateStart' />
            <Input
              id='targetDateStart'
              name='targetDateStart'
              type='number'
              placeholder={t('Target start day')}
              value={form.targetDateStart}
              onChange={handleChangeForm}
              max={targetMonthDays}
              fullWidth />
            <div className={styles.description}>
              {t('{{month}} has {{days}} days', {month: moment(currentDate, 'YYYY-MM').format('MMMM'), days: targetMonthDays})}
            </div>
          </div>
          <div>
            <Label text={t('If target days already have entries:')} htmlFor='overwriteExisting' />
            <RadioButton
              className={styles.radioGroup}
              options={[{name: 'skip', id: 'skip', label: t('Skip conflicting days')}, {name: 'overwrite', id: 'overwrite', label: t('Overwrite them')}]}
              checked={form.overwriteExisting}
              onChange={handleChangeOverwrite} />
          </div>
        </div>
        {
          sourceStartWeekDay !== targetStartWeekDay
            ? <div className={styles.warningContainer}>
                <AttentionIcon />
                <div dangerouslySetInnerHTML={{
                  __html: t(`Weekdays don't line up — source starts on <b>{{sourceStartWeekDay}}</b>, target starts on <b>{{targetStartWeekDay}}</b>.`, {sourceStartWeekDay: sourceStartWeekDay, targetStartWeekDay: targetStartWeekDay})
                }} />
              </div>
            : null
        }
        <div className={styles.footer}>
          <Button cancel onClick={handleClose}>
            { t('Cancel') }
          </Button>
          <Button
            onClick={handleCopySchedule}
            loading={loading}
            disabled={sourceEvents.length === 0 || form.days <= 0 || form.sourceStartDate <= 0 || form.targetDateStart <= 0 || form.sourceStartDate > sourceMonthDays || form.targetDateStart > targetMonthDays || form.days > maxDays}>
            { t('Copy schedule') }
          </Button>
        </div>
      </div>
    </Dialog>
  )
})

export default CopySchedule
